/**
 * Enhanced useCounters Hook with Adaptive Polling
 * HTTP-first integration for UI counters (badges)
 * Supports adaptive polling for "near real-time" updates
 * 
 * Features:
 * - Adaptive polling (fast when active, slow when idle)
 * - Smart refetch after user actions
 * - Pause when tab is hidden
 * - Manual refetch capability
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import lib from '../assets/utils/lib';
import { getNotifyClient } from '../features/notify';

/**
 * Live counters (WebSocket). While the notification socket is connected the
 * server pushes `counters.sync` snapshots whenever a count changes, so the poll
 * drops to a slow safety net. Snapshots and HTTP responses both carry the server
 * time in ms; whichever is newer wins, so an in-flight poll never overwrites a
 * fresher push. The per-person notification count always comes from the poll.
 */
const LIVE_FALLBACK_MS = 300000;

/**
 * @typedef {Object} CountersResponse
 * @property {Object} [notifications] - Notification counters
 * @property {number} [notifications.unread] - Unread notifications count
 * @property {Object} [pa] - PA counters
 * @property {number} [pa.pending] - Pending PA count
 * @property {number} [pa.queried] - Queried PA count
 * @property {Object} [claims] - Claims counters
 * @property {number} [claims.pending_review] - Pending claims count
 * @property {Object} [appointments] - Appointment counters
 * @property {number} [appointments.upcoming] - Upcoming appointments (client)
 * @property {number} [appointments.pending_confirmation] - Pending confirmations (facility)
 * @property {number} [appointments.today] - Today's appointments (facility)
 * @property {Object} [enrollees] - Enrollee counters
 * @property {number} [enrollees.new] - New enrollees count
 * @property {number} [enrollees.updated] - Updated enrollees count
 */

/**
 * Enhanced counters hook with adaptive polling
 * @param {string|null} token - JWT token for authentication
 * @param {number|Object} intervalOrOptions - Either interval in ms OR options object
 * @param {number} [intervalOrOptions.interval=30000] - Fixed polling interval (ms)
 * @param {boolean} [intervalOrOptions.adaptive=false] - Enable adaptive polling
 * @param {number} [intervalOrOptions.fastInterval=15000] - Fast polling when active (ms)
 * @param {number} [intervalOrOptions.slowInterval=60000] - Slow polling when idle (ms)
 * @param {number} [intervalOrOptions.idleTimeout=120000] - Time before switching to slow (ms)
 * @param {boolean} [intervalOrOptions.pauseWhenHidden=true] - Pause when tab is hidden
 * @returns {Object} { counters, loading, error, refetch, isActive, currentInterval }
 */
export function useCountersEnhanced(token, intervalOrOptions = 30000) {
  // Parse options
  const options = typeof intervalOrOptions === 'number' 
    ? { interval: intervalOrOptions, adaptive: false }
    : { 
        interval: 60000,
        adaptive: false,
        fastInterval: 15000,
        slowInterval: 60000,
        idleTimeout: 120000,
        pauseWhenHidden: true,
        ...intervalOrOptions,
      };

  const [counters, setCounters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [currentInterval, setCurrentInterval] = useState(
    options.adaptive ? options.fastInterval : options.interval
  );

  const intervalRef = useRef(null);
  const cancelledRef = useRef(false);
  const lastActivityRef = useRef(Date.now());
  const idleTimerRef = useRef(null);
  const lastAtRef = useRef(0);
  const fetchRef = useRef(null);
  const [live, setLive] = useState(false);

  // Memoize fetchCounters to prevent stale closures
  const fetchCounters = useCallback(async () => {
    if (!token) {
      // console.log('[useCountersEnhanced] No token, skipping fetch');
      setLoading(false);
      return;
    }

    if (cancelledRef.current) {
      // console.log('[useCountersEnhanced] Already cancelled, skipping fetch');
      return;
    }

    // Pause if tab is hidden and pauseWhenHidden is enabled
    if (options.pauseWhenHidden && document.hidden) {
      // console.log('[useCountersEnhanced] Tab hidden, skipping fetch');
      return;
    }

    // console.log('[useCountersEnhanced] Fetching counters from /v2/ui/counters');
    try {
      const response = await lib.getUICounters();
      
      if (cancelledRef.current) {
        // console.log('[useCountersEnhanced] Request cancelled');
        return;
      }

      const body = response?.data || response;

      if (parseInt(body?.error) === 0) {
        const data = body?.data || {};
        const at = Number(body?.generated_at) || 0;
        if (at && at < lastAtRef.current) {
          // A live push landed after this request was served: keep its numbers,
          // take only the per-person notification count from the poll.
          setCounters((prev) => ({ ...(prev || {}), notifications: data.notifications }));
        } else {
          if (at) lastAtRef.current = at;
          setCounters(data);
        }
        setError(null);
      } else {
        throw new Error(body?.message || 'Failed to fetch counters');
      }
    } catch (err) {
      if (!cancelledRef.current) {
        // console.error('[useCountersEnhanced] Error fetching UI counters:', err);
        setError(err);
      }
    } finally {
      if (!cancelledRef.current) {
        setLoading(false);
      }
    }
  }, [token, options.pauseWhenHidden]);

  fetchRef.current = fetchCounters;

  // Live snapshots over the notification WebSocket.
  useEffect(() => {
    if (!token) return undefined;
    const client = getNotifyClient();
    if (!client) return undefined;
    const isLive = (s) => Boolean(s?.enabled && s?.connected);
    let wasLive = isLive(client.getState());
    setLive(wasLive);
    return client.subscribe((type, payload, s) => {
      if (type === 'counters' && payload?.counters) {
        const at = Number(payload.at) || Date.now();
        if (at < lastAtRef.current) return;
        lastAtRef.current = at;
        setCounters((prev) => ({ ...(prev || {}), ...payload.counters }));
        setLoading(false);
        return;
      }
      if (type === 'state') {
        const nowLive = isLive(s);
        // (Re)connected: resync once over HTTP, pushes may have been missed.
        if (nowLive && !wasLive && fetchRef.current) fetchRef.current();
        wasLive = nowLive;
        setLive(nowLive);
      }
    });
  }, [token]);

  // Adaptive polling: switch between fast and slow intervals
  const switchToFast = useCallback(() => {
    if (!options.adaptive || currentInterval === options.fastInterval) return;
    
    // console.log('[useCountersEnhanced] Switching to FAST polling:', options.fastInterval, 'ms');
    setCurrentInterval(options.fastInterval);
    setIsActive(true);
    lastActivityRef.current = Date.now();
  }, [options.adaptive, options.fastInterval, currentInterval]);

  const switchToSlow = useCallback(() => {
    if (!options.adaptive || currentInterval === options.slowInterval) return;
    
    // console.log('[useCountersEnhanced] Switching to SLOW polling:', options.slowInterval, 'ms');
    setCurrentInterval(options.slowInterval);
    setIsActive(false);
  }, [options.adaptive, options.slowInterval, currentInterval]);

  // Mark user activity (for adaptive polling)
  const markActivity = useCallback(() => {
    if (!options.adaptive) return;
    
    lastActivityRef.current = Date.now();
    switchToFast();
    
    // Clear existing idle timer
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    
    // Set new idle timer
    idleTimerRef.current = setTimeout(() => {
      const timeSinceActivity = Date.now() - lastActivityRef.current;
      if (timeSinceActivity >= options.idleTimeout) {
        switchToSlow();
      }
    }, options.idleTimeout);
  }, [options.adaptive, options.idleTimeout, switchToFast, switchToSlow]);

  // Setup polling interval
  useEffect(() => {
    if (!token) {
      // console.log('[useCountersEnhanced] No token, clearing counters');
      setCounters(null);
      setLoading(false);
      return;
    }

    cancelledRef.current = false;
    setLoading(true);

    // Initial fetch
    // console.log('[useCountersEnhanced] Starting initial fetch');
    fetchCounters();

    // Setup polling interval (slow safety net while live snapshots arrive)
    const effectiveInterval = live ? LIVE_FALLBACK_MS : currentInterval;
    if (effectiveInterval > 0) {
      // console.log('[useCountersEnhanced] Setting up polling interval:', currentInterval, 'ms');
      
      intervalRef.current = setInterval(() => {
        if (!cancelledRef.current && token) {
          // Skip if tab is hidden and pauseWhenHidden is enabled
          if (options.pauseWhenHidden && document.hidden) {
            return;
          }
          //  console.log('[useCountersEnhanced] Polling interval triggered');
          fetchCounters();
        }
      }, effectiveInterval);
    }

    return () => {
      // console.log('[useCountersEnhanced] Cleanup: cancelling and clearing interval');
      cancelledRef.current = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [token, currentInterval, live, fetchCounters, options.pauseWhenHidden]);

  // Handle visibility changes (tab hidden/visible)
  useEffect(() => {
    if (!options.pauseWhenHidden) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        //  console.log('[useCountersEnhanced] Tab hidden - polling will pause');
      } else {
        // console.log('[useCountersEnhanced] Tab visible - resuming polling');
        if (options.adaptive) {
          markActivity(); // Resume with fast polling
        } else {
          fetchCounters(); // Immediate fetch
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [options.pauseWhenHidden, options.adaptive, markActivity, fetchCounters]);

  // Track user activity for adaptive polling
  useEffect(() => {
    if (!options.adaptive) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      markActivity();
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [options.adaptive, markActivity]);

  // Manual refetch function (also marks activity)
  const refetch = useCallback(() => {
    if (token) {
      if (options.adaptive) {
        markActivity();
      }
      fetchCounters();
    }
  }, [token, options.adaptive, markActivity, fetchCounters]);

  return {
    counters,
    loading,
    error,
    refetch,
    isActive, // true = fast polling, false = slow polling
    currentInterval: live ? LIVE_FALLBACK_MS : currentInterval, // current polling interval in ms
    live, // true = counters arrive over the WebSocket
  };
}

export default useCountersEnhanced;

